import { ExpiryMap } from '../optimizations/dataStructures';
import type { SupportedLanguages } from '../../i18n';
import { logger } from '../logger';

interface CachedTranslation {
  text: string;
  timestamp: number;
  source: 'api' | 'db' | 'fallback';
}

export class TranslationCache {
  private static instance: TranslationCache;
  private cache: ExpiryMap<string, Record<SupportedLanguages, CachedTranslation>>;
  private partialCache: Map<string, Set<string>> = new Map();
  private readonly PARTIAL_MATCH_THRESHOLD = 0.8;

  private constructor() {
    this.cache = new ExpiryMap(30 * 60 * 1000); // 30 minutes TTL
  }

  public static getInstance(): TranslationCache {
    if (!TranslationCache.instance) {
      TranslationCache.instance = new TranslationCache();
    }
    return TranslationCache.instance;
  }

  public get(
    text: string,
    targetLang: SupportedLanguages
  ): CachedTranslation | null {
    const cached = this.cache.get(text);
    if (!cached) return null;

    const translation = cached[targetLang];
    if (!translation) return null;

    // Vérifier si la traduction n'est pas expirée
    if (Date.now() - translation.timestamp > 30 * 60 * 1000) {
      return null;
    }

    return translation;
  }

  public set(
    text: string,
    targetLang: SupportedLanguages,
    translation: string,
    source: 'api' | 'db' | 'fallback' = 'api'
  ): void {
    const cached = this.cache.get(text) || {};
    
    cached[targetLang] = {
      text: translation,
      timestamp: Date.now(),
      source
    };

    this.cache.set(text, cached);
    this.updatePartialCache(text);
  }

  private updatePartialCache(text: string): void {
    // Créer des segments de texte pour la recherche partielle
    const segments = this.createTextSegments(text);
    
    segments.forEach(segment => {
      if (!this.partialCache.has(segment)) {
        this.partialCache.set(segment, new Set());
      }
      this.partialCache.get(segment)!.add(text);
    });
  }

  private createTextSegments(text: string): string[] {
    const segments = new Set<string>();
    const words = text.toLowerCase().split(/\s+/);

    // Ajouter des segments de différentes tailles
    for (let i = 0; i < words.length; i++) {
      for (let j = i + 1; j <= Math.min(i + 5, words.length); j++) {
        segments.add(words.slice(i, j).join(' '));
      }
    }

    return Array.from(segments);
  }

  public findSimilarTranslations(
    text: string,
    targetLang: SupportedLanguages
  ): CachedTranslation | null {
    const segments = this.createTextSegments(text);
    const candidates = new Map<string, number>();

    // Trouver les textes similaires dans le cache
    segments.forEach(segment => {
      const matches = this.partialCache.get(segment);
      if (matches) {
        matches.forEach(match => {
          candidates.set(match, (candidates.get(match) || 0) + 1);
        });
      }
    });

    if (candidates.size === 0) return null;

    // Trouver le meilleur candidat
    let bestMatch: string | null = null;
    let bestScore = 0;

    candidates.forEach((score, candidate) => {
      const similarity = this.calculateSimilarity(text, candidate);
      const weightedScore = score * similarity;

      if (weightedScore > bestScore) {
        bestScore = weightedScore;
        bestMatch = candidate;
      }
    });

    if (bestMatch && bestScore >= this.PARTIAL_MATCH_THRESHOLD) {
      const cached = this.get(bestMatch, targetLang);
      if (cached) {
        logger.debug('Found similar translation', {
          category: 'Translation',
          data: { original: text, match: bestMatch, score: bestScore }
        });
        return cached;
      }
    }

    return null;
  }

  private calculateSimilarity(text1: string, text2: string): number {
    const words1 = new Set(text1.toLowerCase().split(/\s+/));
    const words2 = new Set(text2.toLowerCase().split(/\s+/));

    const intersection = new Set(
      [...words1].filter(word => words2.has(word))
    );

    return intersection.size / Math.max(words1.size, words2.size);
  }

  public clear(): void {
    this.cache = new ExpiryMap(30 * 60 * 1000);
    this.partialCache.clear();
  }
}

export const translationCache = TranslationCache.getInstance();